import { useRef } from "react";
import { Link } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Netflix-style Top 10 numbered rail.
 * Each card is a portrait poster with a giant outlined numeral peeking out
 * from BEHIND the poster. The numeral is text-stroke white, no fill.
 */
export default function Top10Rail({
  title,
  items,
  tenantSlug,
  themeColor = "#7C3AED",
  testid = "top10-rail",
}) {
  const scrollerRef = useRef(null);
  const list = (items || []).slice(0, 10);
  if (list.length === 0) return null;

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };

  return (
    <section className="px-6 md:px-10 py-6 group/row" data-testid={testid}>
      <div className="flex items-end justify-between mb-4">
        <h2 className="font-display font-black text-xl md:text-2xl tracking-tight">
          {title}
        </h2>
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/30">
          {list.length} title{list.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="relative">
        <button
          aria-label="Scroll left"
          onClick={() => scroll(-1)}
          className="hidden md:grid place-items-center absolute -left-1 top-0 bottom-0 z-20 w-10 bg-black/70 hover:bg-black/90 opacity-0 group-hover/row:opacity-100 transition-opacity"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div
          ref={scrollerRef}
          className="flex gap-2 md:gap-4 overflow-x-auto pb-4 pt-10 snap-x"
          style={{ scrollbarWidth: "thin" }}
        >
          {list.map((it, idx) => (
            <Top10Card
              key={it.id || it.slug}
              item={it}
              rank={idx + 1}
              tenantSlug={tenantSlug}
              themeColor={themeColor}
            />
          ))}
        </div>
        <button
          aria-label="Scroll right"
          onClick={() => scroll(1)}
          className="hidden md:grid place-items-center absolute -right-1 top-0 bottom-0 z-20 w-10 bg-black/70 hover:bg-black/90 opacity-0 group-hover/row:opacity-100 transition-opacity"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </section>
  );
}

function Top10Card({ item, rank, tenantSlug, themeColor }) {
  const isReel = item.kind === "reel";
  const { sp } = useStore();
  const href = isReel ? sp(`/r/${item.slug}`) : sp(`/title/${item.slug}`);
  const poster = item.poster_portrait_url || item.thumbnail_url || item.hero_image_url || "";
  return (
    <Link
      to={href}
      className="snap-start relative flex items-center pl-[64px] md:pl-[100px] flex-shrink-0 group"
      data-testid={`top10-card-${item.slug}`}
    >
      {/* Giant outlined numeral peeking from BEHIND the poster */}
      <span
        aria-hidden="true"
        className="absolute left-0 bottom-[-4px] md:bottom-[-12px] z-0 leading-none font-black select-none pointer-events-none"
        style={{
          fontSize: "clamp(140px, 22vw, 240px)",
          color: "transparent",
          WebkitTextStroke: "3px rgba(255,255,255,0.85)",
          fontFamily: '"Cabinet Grotesk", "Inter Display", Inter, system-ui, sans-serif',
          letterSpacing: "-0.05em",
        }}
      >
        {rank}
      </span>
      {/* Portrait poster card */}
      <div
        className="relative z-10 w-[120px] md:w-[180px] aspect-[2/3] overflow-hidden bg-[#121212] border border-white/15 group-hover:border-white/40 transition-all duration-200 group-hover:-translate-y-1"
        style={{
          boxShadow:
            "0 10px 40px -10px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.05) inset",
        }}
      >
        {poster ? (
          <img
            src={poster}
            alt={item.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${themeColor}55, #0a0a0a 70%)` }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-90" />
        <div className="absolute bottom-0 inset-x-0 p-3">
          <h3 className="font-display font-bold text-xs md:text-sm tracking-tight leading-tight line-clamp-2">
            {item.title}
          </h3>
        </div>
      </div>
    </Link>
  );
}
